// apps/web/src/components/NotificationCenter/NotificationCenter.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiRequest } from '../../services/api';
import './NotificationCenter.css';

function timeAgo(dateString) {
    const seconds = Math.floor((Date.now() - new Date(dateString)) / 1000);
    if (seconds < 60)    return 'just now';
    if (seconds < 3600)  return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
}

export default function NotificationCenter() {
    const [open, setOpen]               = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading]         = useState(false);
    const panelRef = useRef(null);
    const navigate = useNavigate();

    // apiRequest adds the stored access token, refreshes it when it expires
    // and resolves paths against REACT_APP_API_URL (…/api/v1). Background
    // calls are quiet so a dropped connection doesn't raise the offline banner.
    const fetchUnreadCount = useCallback(async () => {
        try {
            // apiRequest returns the response's `data` (here `{ unreadCount }`)
            const res = await apiRequest('/notifications/unread-count', { quiet: true });
            if (Number.isFinite(res?.unreadCount)) setUnreadCount(res.unreadCount);
        } catch { /* silent */ }
    }, []);

    const fetchNotifications = useCallback(async () => {
        setLoading(true);
        try {
            const list = await apiRequest('/notifications', { params: { limit: 30 }, quiet: true });
            if (Array.isArray(list)) setNotifications(list);
        } catch { /* silent */ }
        setLoading(false);
    }, []);

    // Poll the unread count every 60s, and refresh when the tab is shown again
    useEffect(() => {
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 60_000);
        const onVisible = () => { if (document.visibilityState === 'visible') fetchUnreadCount(); };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [fetchUnreadCount]);

    // Close on outside click
    useEffect(() => {
        function handleClick(e) {
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        if (open) document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    const handleOpen = () => {
        setOpen((prev) => !prev);
        if (!open) fetchNotifications();
    };

    const handleMarkRead = async (id) => {
        try {
            await apiRequest(`/notifications/${id}/read`, { method: 'PATCH' });
            setNotifications((prev) =>
                prev.map((n) => (n.id === id ? { ...n, status: 'read' } : n))
            );
            setUnreadCount((c) => Math.max(0, c - 1));
        } catch { /* silent */ }
    };

    const handleMarkAllRead = async () => {
        try {
            await apiRequest('/notifications/mark-all-read', { method: 'PATCH' });
            setNotifications((prev) => prev.map((n) => ({ ...n, status: 'read' })));
            setUnreadCount(0);
        } catch { /* silent */ }
    };

    const handleNotificationClick = (notification) => {
        if (notification.status !== 'read') {
            handleMarkRead(notification.id);
        }
        const deepLink = notification.data?.deepLink;
        if (deepLink) navigate(deepLink);
        setOpen(false);
    };

    return (
        <div className="nc-wrapper" ref={panelRef}>
            {/* Bell button */}
            <button
                type="button"
                className="nc-bell"
                onClick={handleOpen}
                aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
                aria-expanded={open}
            >
                <span className="nc-bell-icon">🔔</span>
                {unreadCount > 0 && (
                    <span className="nc-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
            </button>

            {/* Panel */}
            {open && (
                <div className="nc-panel" role="dialog" aria-label="Notifications">
                    <div className="nc-header">
                        <span className="nc-header-title">Notifications</span>
                        {unreadCount > 0 && (
                            <button className="nc-mark-all-btn" onClick={handleMarkAllRead}>
                                Mark all read
                            </button>
                        )}
                    </div>

                    <div className="nc-list">
                        {loading && (
                            <div className="nc-empty">Loading...</div>
                        )}
                        {!loading && notifications.length === 0 && (
                            <div className="nc-empty">No notifications yet.</div>
                        )}
                        {!loading && notifications.map((n) => (
                            <button
                                key={n.id}
                                className={`nc-item ${n.status !== 'read' ? 'nc-item--unread' : ''}`}
                                onClick={() => handleNotificationClick(n)}
                            >
                                <div className="nc-item-icon">{getIcon(n.type)}</div>
                                <div className="nc-item-content">
                                    <div className="nc-item-title">{n.title}</div>
                                    <div className="nc-item-body">{n.body}</div>
                                    <div className="nc-item-time">{timeAgo(n.created_at)}</div>
                                </div>
                                {n.status !== 'read' && <div className="nc-unread-dot" />}
                            </button>
                        ))}
                    </div>

                    <div className="nc-footer">
                        <Link to="/settings/notifications" className="nc-settings-link" onClick={() => setOpen(false)}>
                            ⚙️ Notification settings
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
}

function getIcon(type) {
    const icons = {
        legendary_attempt_ready:    '💎',
        coin_upgrade_ready:         '🔥',
        collection_almost_complete: '💎',
        collection_completed:       '🏆',
        series_mastery_unlocked:    '📺',
        watchlist_ready_to_verify:  '🎬',
        collection_halfway:         '⚡',
        series_mastery_almost_complete: '⭐',
        reengagement_7day:          '👋',
        weekly_summary:             '📊',
        welcome:                    '👋',
        achievement_reaction:       '🔥',
        new_follower:               '👀',
        friend_major_achievement:   '👑',
        weekly_digest:              '📬',
        milestone_email:            '🏅',
    };
    return icons[type] || '🔔';
}